import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Plus, Clock, Users, Cog, Timer, RefreshCw, Search, LayoutDashboard, Database } from "lucide-react";
import TimesheetForm from "@/components/TimesheetForm";
import TimesheetTable from "@/components/TimesheetTable";
import DatabaseMonitor from "@/components/DatabaseMonitor";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [adminName, setAdminName] = useState("Admin");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState("timesheet"); // "timesheet" or "database"
  const [stats, setStats] = useState({
    totalEntries: 0,
    uniqueEmployees: 0,
    uniqueMachines: 0,
    totalHours: 0,
  });

  useEffect(() => {
    const storedAdmin = localStorage.getItem('adminName') || 'Admin';
    setAdminName(storedAdmin);
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [selectedDate]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/timesheet?status=completed&date=${selectedDate}`);
      setEntries(response.data);
      calculateStats(response.data);
    } catch (error) {
      console.error("Error fetching entries:", error);
      toast.error("Failed to load timesheet entries");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEntries();
    setRefreshing(false);
  };

  const calculateStats = (entriesData) => {
    const uniqueEmployees = new Set(entriesData.map((e) => e.employee_name));
    const uniqueMachines = new Set(entriesData.map((e) => e.machine_id));

    let totalSeconds = 0;
    entriesData.forEach((entry) => {
      if (!entry.end_time || !entry.start_time) return;
      
      const parseTime = (timeStr) => {
        const parts = timeStr.split(':').map(Number);
        return {
          hours: parts[0] || 0,
          minutes: parts[1] || 0,
          seconds: parts[2] || 0
        };
      };
      
      const start = parseTime(entry.start_time);
      const end = parseTime(entry.end_time);
      
      const startSeconds = start.hours * 3600 + start.minutes * 60 + start.seconds;
      const endSeconds = end.hours * 3600 + end.minutes * 60 + end.seconds;
      
      let duration = endSeconds - startSeconds;
      if (duration < 0) {
        duration += 24 * 3600;
      }
      
      totalSeconds += duration;
    });

    const totalHours = totalSeconds / 3600;

    setStats({
      totalEntries: entriesData.length,
      uniqueEmployees: uniqueEmployees.size,
      uniqueMachines: uniqueMachines.size,
      totalHours: totalHours.toFixed(1),
    });
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleAddEntry = () => {
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingEntry(null);
    fetchEntries();
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/timesheet/${entryId}`);
      toast.success("Entry deleted successfully");
      fetchEntries();
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete entry");
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-blue-400 flex items-center gap-3">
              <LayoutDashboard className="h-8 w-8" />
              Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Manage timesheet and monitor system health</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Tab Buttons */}
            <div className="flex bg-gray-800 rounded-lg p-1">
              <Button
                onClick={() => setActiveTab("timesheet")}
                variant={activeTab === "timesheet" ? "default" : "ghost"}
                className={`px-4 py-2 ${activeTab === "timesheet" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-white"}`}
              >
                <Clock className="h-4 w-4 mr-2" />
                Timesheet
              </Button>
              <Button
                onClick={() => setActiveTab("database")}
                variant={activeTab === "database" ? "default" : "ghost"}
                className={`px-4 py-2 ${activeTab === "database" ? "bg-cyan-500 text-white" : "text-gray-400 hover:text-white"}`}
              >
                <Database className="h-4 w-4 mr-2" />
                Database Monitor
              </Button>
            </div>
          </div>
        </div>

        {activeTab === "database" ? (
          <DatabaseMonitor />
        ) : (
          <>
            {/* Timesheet Controls */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-400">Date:</label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white w-40 [color-scheme:dark]"
                />
              </div>
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
                disabled={refreshing}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
                Refresh
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gray-800/50 border border-blue-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <Clock className="h-8 w-8 text-blue-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{stats.totalEntries}</p>
                  <p className="text-sm text-gray-400">Total Entries</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border border-green-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <Users className="h-8 w-8 text-green-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{stats.uniqueEmployees}</p>
                  <p className="text-sm text-gray-400">Employees</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border border-purple-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <Cog className="h-8 w-8 text-purple-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{stats.uniqueMachines}</p>
                  <p className="text-sm text-gray-400">Machines</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border border-slate-600">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <Timer className="h-8 w-8 text-cyan-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{stats.totalHours}h</p>
                  <p className="text-sm text-gray-400">Total Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add Entry Button */}
        <div className="flex gap-3">
          <Button
            data-testid="add-entry-button"
            onClick={handleAddEntry}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </div>

        {/* Form Dialog */}
        {showForm && (
          <Card className="bg-gray-800/50 border border-gray-700">
            <CardHeader className="border-b border-gray-700">
              <CardTitle className="text-xl text-white">
                {editingEntry ? "Edit Entry" : "Add New Entry"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <TimesheetForm
                onSuccess={handleFormSuccess}
                entry={editingEntry}
                onCancel={() => setShowForm(false)}
              />
            </CardContent>
          </Card>
        )}

        {/* Timesheet Table */}
        <Card className="bg-gray-800/50 border border-gray-700">
          <CardHeader className="border-b border-gray-700">
            <CardTitle className="text-xl text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-400" />
              Timesheet Entries ({entries.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent"></div>
                <p className="mt-4 text-gray-400">Loading entries...</p>
              </div>
            ) : (
              <TimesheetTable
                entries={entries}
                onEdit={handleEditEntry}
                onDelete={handleDeleteEntry}
              />
            )}
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
